import * as React from "react";
import { useQuery } from "@tanstack/react-query";
import {
  getAgreementClauseSourceDocumentLink,
  getDecisionSourceDocumentLink,
  getDocumentSectionPdfLink,
  getFindingSourceDocumentLink,
  getSourceDocumentPdfLink,
} from "@hp/api-client";
import type { AssistantSource, SourceDocumentLink } from "@hp/api-client";
import { Button, Dialog } from "@hp/ui";
import { EmptyState, ErrorState, LoadingState } from "./Feedback";

// react-pdf and pdf.js load only when a passage is to be highlighted.
const PassageHighlightView = React.lazy(() => import("./PassageHighlightView").then((m) => ({ default: m.PassageHighlightView })));

export interface SourceDocumentViewerProps {
  /** The clicked answer chip - a "finding" or "document_section" source. */
  source: AssistantSource;
  onClose: () => void;
  /** The cited passage and the PDF page it was read from. Only sources that
   * record a real page of the stored file pass this (Change-in-Law
   * decisions: `page` is the physical page the text was extracted from, and
   * the quote was checked against that page's text). The viewer then opens
   * that page with the passage highlighted. */
  passage?: { text: string; page: number; caution?: string | null; searchWholeDocument?: boolean };
  /** Without a passage: the page the whole document opens at. */
  startPage?: number | null;
}

async function fetchSourceDocumentLink(
  source: AssistantSource,
  startPage: number | null | undefined,
  signal?: AbortSignal,
): Promise<SourceDocumentLink | null> {
  if (source.type === "document_section") {
    return getDocumentSectionPdfLink(source.id, signal);
  }
  if (source.type === "source_document") {
    return getSourceDocumentPdfLink(source.id, startPage, signal);
  }
  if (source.type === "finding") {
    return getFindingSourceDocumentLink(source.id, signal);
  }
  if (source.type === "agreement_clause") {
    return getAgreementClauseSourceDocumentLink(source.id, signal);
  }
  if (source.type === "decision") {
    return getDecisionSourceDocumentLink(source.id, signal);
  }
  return null;
}

// Signed links last 10 minutes server-side. Keep the cached link a bit
// shorter than that so a reopen inside the window reuses the same URL (and
// therefore the browser's HTTP cache of the PDF) but never hands pdf.js an
// expired one.
const LINK_STALE_MS = 8 * 60 * 1000;

/**
 * Opens the real document one answer chip cites - either the
 * object-storage-backed file streamed from
 * `GET /ingestion/findings/{finding_id}/document-url` (a short-lived signed
 * URL to the range-streamed file), or the indexed PDF from
 * `GET /documents/sections/{section_id}/pdf-url`. Shows an honest empty
 * state when this system genuinely has no stored document.
 *
 * Rendered as a plain `<iframe>` on the raw PDF URL: the browser's own PDF
 * viewer, not a custom canvas renderer - simpler, and it doesn't fail on
 * files a canvas-based renderer chokes on. For findings and document
 * sections it deliberately does not point at an exact page: those record
 * only a character range in a separately-extracted plain-text copy.
 *
 * A Change-in-Law decision is different: its `page` is the physical page
 * of this same PDF the passage was read from, and the quote was checked
 * against that page. For those (`passage` prop) the viewer opens the page
 * with the passage highlighted (pdf.js, loaded on demand), and falls back
 * to the browser's viewer at that page if pdf.js cannot draw the file.
 */
export function SourceDocumentViewer({ source, onClose, passage, startPage }: SourceDocumentViewerProps) {
  const [mode, setMode] = React.useState<"highlight" | "document">(passage ? "highlight" : "document");
  const [highlightFailed, setHighlightFailed] = React.useState(false);
  const documentQuery = useQuery({
    queryKey: ["source-document-link", source.type, source.id, passage?.page ?? startPage ?? null],
    queryFn: ({ signal }) => fetchSourceDocumentLink(source, passage?.page ?? startPage, signal),
    enabled:
      source.type === "finding" ||
      source.type === "document_section" ||
      source.type === "source_document" ||
      source.type === "agreement_clause" ||
      source.type === "decision",
    staleTime: LINK_STALE_MS,
    gcTime: LINK_STALE_MS,
  });

  const data = documentQuery.data ?? null;
  // `data.url` is always the API's own base URL with a server-issued path appended (see
  // `fetchSourceDocumentLink`) - an absolute http(s) URL in local dev, or a root-relative path like
  // "/api/..." in production, where the app is built with VITE_API_BASE_URL=/api and nginx proxies it
  // same-origin. An iframe `src` is one of the few places a non-http(s) scheme (`javascript:`, `data:`)
  // would actually execute, so this accepts only those two real shapes and nothing else - in particular
  // not a protocol-relative "//host/..." URL, which would silently point at a different origin.
  const fileUrl =
    data?.url && (/^https?:\/\//i.test(data.url) || (data.url.startsWith("/") && !data.url.startsWith("//")))
      ? data.url
      : null;

  const emptyMessage =
    source.type === "document_section" || source.type === "source_document"
      ? "No stored PDF is available for this document."
      : source.type === "decision"
        ? "The document this decision was read from is not in the document store."
        : source.type === "agreement_clause"
        ? "No stored source document is available for this clause. It isn't linked to an uploaded company document - only clauses tied to a real company-document upload run have one to show."
        : "No stored source document is available for this finding. It isn't linked to an ingested file - only findings tied to a real ingestion run (a manual upload or an automated regulatory feed) have one to show.";

  return (
    <Dialog
      open
      onOpenChange={(next) => {
        if (!next) onClose();
      }}
      title={source.label}
      description="Original source document"
      titleId="source-document-viewer-title"
      size="lg"
    >
      <div className="flex flex-col items-center gap-3 bg-zinc-100 -mx-5 -mb-4 px-4 py-4">
        {documentQuery.isPending ? <LoadingState label="Opening source document..." /> : null}

        {documentQuery.isError ? (
          <ErrorState error={documentQuery.error} label="Could not open the source document" />
        ) : null}

        {documentQuery.isSuccess && data === null ? <EmptyState>{emptyMessage}</EmptyState> : null}

        {documentQuery.isSuccess && data && !fileUrl ? (
          <ErrorState
            error={new Error("The document link the server returned was not a valid URL.")}
            label="Could not open the source document"
          />
        ) : null}

        {documentQuery.isSuccess && data && fileUrl && passage ? (
          <div className="flex w-full flex-col gap-2 rounded-md border border-amber-200 bg-amber-50/70 p-3 text-sm">
            <div className="flex flex-wrap items-center gap-2">
              <span className="font-semibold text-zinc-900">{passage.searchWholeDocument ? "One of the possible cited passages" : `One of the possible citations on page ${passage.page}`}</span>
              <span className="text-zinc-600">of {data.fileName}</span>
              <div className="ml-auto flex gap-1" role="tablist" aria-label="How to show the document">
                <Button size="sm" variant={mode === "highlight" ? "primary" : "outline"} disabled={highlightFailed} onClick={() => setMode("highlight")} role="tab" aria-selected={mode === "highlight"}>
                  Highlighted page
                </Button>
                <Button size="sm" variant={mode === "document" ? "primary" : "outline"} onClick={() => setMode("document")} role="tab" aria-selected={mode === "document"}>
                  Whole document
                </Button>
              </div>
            </div>
            <blockquote className="border-l-2 border-amber-400 pl-3 italic text-zinc-800">“{passage.text}”</blockquote>
            {passage.caution ? <p className="text-xs text-amber-800">{passage.caution}</p> : null}
            {highlightFailed ? (
              <p className="text-xs text-amber-800">
                This file could not be drawn page by page here, so it is shown in the browser's viewer at page {passage.page}. Use Ctrl/⌘+F with a few words of the passage to find it on the page.
              </p>
            ) : null}
          </div>
        ) : null}

        {documentQuery.isSuccess && data && fileUrl && passage && mode === "highlight" && !highlightFailed ? (
          <React.Suspense fallback={<LoadingState label="Opening the page..." />}>
            <PassageHighlightView
              fileUrl={fileUrl}
              page={passage.page}
              passage={passage.text}
              searchWholeDocument={passage.searchWholeDocument}
              onFail={() => {
                setHighlightFailed(true);
                setMode("document");
              }}
            />
          </React.Suspense>
        ) : null}

        {documentQuery.isSuccess && data && fileUrl && (!passage || mode === "document") ? (
          <iframe
            key={mode}
            title={source.label}
            // `#page=` is honoured by the Chrome, Safari and Firefox viewers.
            src={passage ? `${fileUrl}#page=${passage.page}` : startPage ? `${fileUrl}#page=${startPage}` : fileUrl}
            className="h-[75vh] w-full rounded-md border border-zinc-200 bg-white"
          />
        ) : null}
      </div>
    </Dialog>
  );
}
